// --- START OF FILE frontend/src/api/dfs.ts ---
// REVISED: Added fetchActiveContests, fetchPastContests.
//          fetchUpcomingContests now uses the /dfs/contests/upcoming endpoint.

import { ApiError } from './auth'; // Import the custom error
import { createApiClient, isAxiosError, isCancel } from './client';
import { PaginatedAvailablePlayersResponse } from './schemas';

// --- Configuration & Interfaces (Ensure these are consistent with your actual definitions) ---
import { API_BASE_URL } from '@/config/apiConfig';

import { ApiFantasyScore, ContestMatchupPreviewResponse } from './schemas';

// const API_BASE_URL = 'http://localhost:8000/api/v1';
console.log("Using API Base URL for DFS:", API_BASE_URL);

// ApiPlayer Interface (Keep your existing definition)
export interface ApiPlayer {
    id: number;
    name?: string | null;         // Stricter: string | null
    nickname?: string | null;     // Stricter: string | null
    first_name?: string | null;   // Stricter: string | null
    last_nam?: string | null;    // Stricter: string | null
    
    current_team_id?: number | null;    // Allow null if player is free agent in DFS pool
    current_team_name?: string | null; // Allow null
    primary_position?: string | null;  // Player's general position
    nationality?: string | null;     // Optional
    dob?: string | null;             // Optional

    // DFS specific fields (these are key for DFS PlayerPool)
    dfs_salary?: number | null;        // Salary for THIS contest
    fppg?: number | null;              // FPPG for THIS contest/slate
    oprk?: number | null;              // OPRK for THIS contest/slate
    
    injury_status?: string | null;
    injury_details?: string | null;

    is_starting?: boolean | null;
    availability_status?: 'STARTING' | 'BENCH' | 'NOT_IN_SQUAD' | 'LINEUPS_TBD' | null;

    // Dynamic context fields for DFS displays
    current_score?: number | null;    // Player's live score in the DFS contest
    opponent?: string | null;         // Opponent for the DFS contest match
    match_time?: string | null;       // Match time for the DFS contest match
    match_status_code?: string | null;// Match status for the DFS contest match

    // Live match score fields (from ESPN)
    home_score?: number | null;
    away_score?: number | null;
    match_clock?: string | null;      // e.g. "23'", "45'+1'", "FT"
    home_team_abbr?: string | null;   // e.g. "AVL"
    away_team_abbr?: string | null;   // e.g. "WOL"
}

// Corresponds to backend schema: schemas.dfs.PlayerInMatchWithScore
export interface ApiPlayerInMatchWithScore extends ApiPlayer { // Extends ApiPlayer
  match_fantasy_points: number | null;
  // Season-long league context (only when fetched with a leagueId): mirrors
  // PlayerRead's ownership/availability contract from the Players page.
  owner_team_id?: number | null;
  owner_team_name?: string | null;
  current_league_availability_status?: 'WAIVER' | 'FCFS' | 'ROSTERED' | string | null;
  waiver_clears_at?: string | null;
  fantasy_points?: number | null; // Season total fantasy points from PlayerSeasonStats
  goals_in_match?: number; // Number of goals scored in this match
  match_team_id?: number | null; // Immutable club represented in this match
}

// AvailablePlayersApiResponse (Keep your existing definition)
export interface AvailablePlayersApiResponse {
    contest_id: number | null;
    contest_date: string | null; // YYYY-MM-DD format
    contest_start_time_utc: string | null; // ISO format UTC
    players: ApiPlayer[];
    salary_cap?: number;
}

// ApiDfsContest (Ensure this matches your schemas/dfs.py -> DfsContestRead)
export interface ApiDfsContest {
    id: number;
    name: string;
    contest_date: string; // YYYY-MM-DD
    salary_cap: number;
    relevant_match_ids: number[] | null;
    created_at?: string | null; // ISO datetime
    updated_at?: string | null; // ISO datetime
    start_time?: string | null; // ISO datetime UTC
    is_weekend_contest?: boolean;
    is_finalized?: boolean; // Important for past contests
    competitions?: ApiCompetitionSlim[] | null;
    user_lineup_id?: number | null; // Current user's lineup ID for this contest, if entered
    user_score?: number | null; // User's current score in this contest
    user_rank?: number | null; // User's rank in this contest
    total_entries?: number | null; // Total entries in this contest
    last_match_start_time?: string | null; // UTC kickoff of the last match in this contest
    venue_slug?: string | null; // a partner bar's private contest (BAR-CONTESTS-SCOPE)
}

// Other Existing Interfaces (Keep them as they are)
export interface LineupSubmissionPlayer { player_id: number; assigned_position: string; }
export interface LineupSubmissionPayload { contest_id: number; players: LineupSubmissionPlayer[]; /** A bar contest's entry code. */ venue_code?: string; }
export interface SubmittedLineupPlayerDetail { player_id: number; assigned_position: string; player: ApiPlayer | null; }
export interface SubmittedLineupResponse { id: number; user_id: number; contest_id: number; total_salary: number; current_score: number; submitted_at: string; updated_at?: string | null; players: SubmittedLineupPlayerDetail[]; }
export interface ApiRankingEntry { lineup_id: number; user_id: number; username: string | null; rank: number | null; score: number; }
export interface RankingsResponse { contest_id: number; rankings: ApiRankingEntry[]; total_entries?: number | null; }
export interface ApiMatchInfo { match_id: number; match_date: string | null; match_home_team_id: number; match_away_team_id: number; match_home_team_name: string; match_away_team_name: string; match_status_code: string | null; home_score?: number | null; away_score?: number | null; match_clock?: string | null; home_team_abbr?: string | null; away_team_abbr?: string | null; }
export interface ApiCompetitionSlim { // New interface
    id: number;
    name: string;
    short_name: string;
    logo_url?: string | null;
}

// --- Axios Client Setup ---
const apiClient = createApiClient({ baseURL: API_BASE_URL, headers: { 'Content-Type': 'application/json', }, timeout: 5000, });

export const isNetworkOrTimeoutError = (error: unknown): boolean => {
    if (!isAxiosError(error)) return false;
    if (error.code === 'ECONNABORTED') return true;  // axios timeout
    if (error.code === 'ERR_NETWORK') return true;    // DNS/CORS/network down
    if (!error.response) return true;                  // no server response at all
    return false;
};

// --- fetchContestsByDate (Keep your existing, it's useful for specific date lookups if needed elsewhere) ---
export const fetchContestsByDate = async (
    dateString: string,
    token: string | null // <-- 1. Allow token to be null
): Promise<ApiDfsContest[]> => {
    // 2. Remove the initial `if (!token)` check.
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        throw new Error("Invalid date format provided. Use YYYY-MM-DD.");
    }
    const url = `${API_BASE_URL}/dfs/contests/by-date/${dateString}`;
    console.log(`Fetching all contests for date ${dateString} from ${url}`);
    try {
        // 3. Conditionally build the headers object.
        const headers: { [key: string]: string } = {};
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await apiClient.get<ApiDfsContest[]>(url, {
            headers: headers, // Use the conditionally built headers.
        });

        console.log(`Received ${response.data.length} contests for date ${dateString}`);
        return response.data;
    } catch (error: any) {
        let errorMessage = `Failed to fetch contests for date ${dateString}.`;
        let status: number | undefined;
        if (isAxiosError(error)) {
            status = error.response?.status;
            console.error(`Axios error fetching contests for date ${dateString}:`, {
                message: error.message, url: error.config?.url, status, data: error.response?.data,
            });
            if (status === 404) {
                console.info(`No contests found for date ${dateString}.`);
                return [];
            } else if (status === 400) {
                errorMessage = `Invalid date format submitted: ${dateString}.`;
            } else if (status === 401 || status === 403) {
                errorMessage = 'Authentication/Authorization failed. Please log in again.';
            } else {
                const backendError = error.response?.data?.detail;
                if (typeof backendError === 'string') errorMessage = backendError;
            }
        } else {
            console.error(`Unexpected error fetching contests for date ${dateString}:`, error);
        }
        throw new ApiError(errorMessage, status, { cause: error });
    }
};

// --- fetchAvailablePlayers (Keep your existing, already updated to require token) ---
export const fetchAvailablePlayers = async (
  contestIdOrAlias: string | number = 'today',
  token: string | null,
  params?: {
    skip?: number;
    limit?: number;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
    position?: string;
    search?: string;
  }
): Promise<PaginatedAvailablePlayersResponse> => {
    const queryParams = new URLSearchParams();
    if (params) {
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                queryParams.append(key, String(value));
            }
        });
    }
    
    const url = `${API_BASE_URL}/dfs/contests/${contestIdOrAlias}/available-players?${queryParams.toString()}`;
    
    try {
        console.log(`Fetching available players & contest details from: ${url}`);

        const headers: { [key: string]: string } = {};
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await apiClient.get<PaginatedAvailablePlayersResponse>(url, {
             headers: headers
        });
        
        console.log(`Received ${response.data.players.length} of ${response.data.total_available} players.`);
        
        if (!response.data || !Array.isArray(response.data.players)) {
            throw new Error("Invalid data structure received for players from API.");
        }
        return response.data;
    } catch (error: any) {
        let errorMessage = `Failed to load available players for contest '${contestIdOrAlias}'.`;
        let status: number | undefined;
        if (isAxiosError(error)) {
            status = error.response?.status;
            const backendError = error.response?.data?.detail;
            if (status === 403) {
                errorMessage = "Access Denied: You cannot view players for this contest type.";
            } else if (status === 404) {
                errorMessage = `Contest '${contestIdOrAlias}' not found or no players available.`;
            } else if (typeof backendError === 'string') {
                 errorMessage = backendError;
            }
        } else if (error instanceof Error) {
             errorMessage = error.message;
        }
        const userMessage = status === 400 || status === 403
            ? errorMessage
            : status === 404
                ? 'This contest is no longer available.'
                : null;
        throw new ApiError(errorMessage, status, { cause: error, userMessage });
    }
};

// --- fetchUpcomingContests (MODIFIED to use the new endpoint) ---
export const fetchUpcomingContests = async (
    token: string | null, // Accepts a string or null
    days: number = 7
  ): Promise<ApiDfsContest[]> => {
    
    const url = `${API_BASE_URL}/dfs/contests/upcoming?days=${days}`;
    
    try {
      console.log(`Fetching UPCOMING contests from: ${url}`);
      
      // Conditionally build headers object
      const headers: { [key: string]: string } = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await apiClient.get<ApiDfsContest[]>(url, { headers });
      
      console.log(`Received ${response.data.length} upcoming contests.`);
      return response.data;
    } catch (error: any) {
        let errorMessage = `Failed to load upcoming contests.`;
        let status: number | undefined;
        if (isAxiosError(error)) {
            status = error.response?.status;
            console.error("Axios error fetching upcoming contests:", { message: error.message, status, data: error.response?.data });
            if (status === 401 || status === 403) { errorMessage = 'Authentication/Authorization failed.'; }
            else { const backendError = error.response?.data?.detail; if (typeof backendError === 'string') errorMessage = backendError; }
        } else { console.error("Unexpected error fetching upcoming contests:", error); }
        throw new ApiError(errorMessage, status, { cause: error });
    }
  };

// --- *** NEW: fetchActiveContests *** ---
export const fetchActiveContests = async (token: string | null): Promise<ApiDfsContest[]> => {
    
    const url = `${API_BASE_URL}/dfs/contests/active`;
    
    try {
        console.log(`Fetching ACTIVE contests from: ${url}`);
        
        // Conditionally build headers object
        const headers: { [key: string]: string } = {};
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await apiClient.get<ApiDfsContest[]>(url, { headers });
        
        console.log(`Received ${response.data.length} active contests.`);
        return response.data;
    } catch (error: any) {
        let errorMessage = `Failed to load active contests.`;
        let status: number | undefined;
        if (isAxiosError(error)) {
            status = error.response?.status;
            console.error("Axios error fetching active contests:", { message: error.message, status, data: error.response?.data });
            if (status === 401 || status === 403) { errorMessage = 'Authentication/Authorization failed.'; }
            else if (status === 404) {
                console.info("No active contests found (404).");
                return []; // Treat as no active contests
            }
            else { const backendError = error.response?.data?.detail; if (typeof backendError === 'string') errorMessage = backendError; }
        } else { console.error("Unexpected error fetching active contests:", error); }
        
        if (!(status === 404 && isAxiosError(error) && error.response?.data?.detail === "No active contests found.")) {
            throw new ApiError(errorMessage, status);
        }
        return [];
    }
};

// --- fetchEnteredContests ---
export const fetchEnteredContests = async (token: string | null): Promise<ApiDfsContest[]> => {
    if (!token) return []; // Must be authenticated
    const url = `${API_BASE_URL}/dfs/contests/entered`;
    try {
        console.log(`Fetching ENTERED contests from: ${url}`);
        const response = await apiClient.get<ApiDfsContest[]>(url, {
            headers: { Authorization: `Bearer ${token}` },
        });
        console.log(`Received ${response.data.length} entered contests.`);
        return response.data;
    } catch (error: any) {
        let errorMessage = 'Failed to load entered contests.';
        let status: number | undefined;
        if (isAxiosError(error)) {
            status = error.response?.status;
            console.error("Axios error fetching entered contests:", { message: error.message, status, data: error.response?.data });
            const backendError = error.response?.data?.detail;
            if (typeof backendError === 'string') errorMessage = backendError;
        } else { console.error("Unexpected error fetching entered contests:", error); }
        throw new ApiError(errorMessage, status);
    }
};

// --- *** NEW: fetchPastContests *** ---
export const fetchPastContests = async (
    token: string | null,
    daysAgo: number = 30
  ): Promise<ApiDfsContest[]> => {
    
    const url = `${API_BASE_URL}/dfs/contests/past?days_ago=${daysAgo}`;
    
    try {
        console.log(`Fetching PAST contests (last ${daysAgo} days) from: ${url}`);
        
        // Conditionally build headers object
        const headers: { [key: string]: string } = {};
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await apiClient.get<ApiDfsContest[]>(url, { headers });
        
        console.log(`Received ${response.data.length} past contests.`);
        return response.data;
    } catch (error: any) {
        let errorMessage = `Failed to load past contests.`;
        let status: number | undefined;
        if (isAxiosError(error)) {
            status = error.response?.status;
            console.error("Axios error fetching past contests:", { message: error.message, status, data: error.response?.data });
            if (status === 401 || status === 403) { errorMessage = 'Authentication/Authorization failed.'; }
            else { const backendError = error.response?.data?.detail; if (typeof backendError === 'string') errorMessage = backendError; }
        } else { console.error("Unexpected error fetching past contests:", error); }
        throw new ApiError(errorMessage, status);
    }
};

// --- Other existing API functions (submitLineup, updateMyLineup, fetchMyTodaysLineup, etc.) ---
// --- (Keep them as they are, they should be compatible with the new backend logic or already updated) ---
export const submitLineup = async ( payload: LineupSubmissionPayload, token: string ): Promise<SubmittedLineupResponse> => {
    if (!token) throw new ApiError("Authentication required to submit lineup.", 401);
    const url = `${API_BASE_URL}/dfs/lineups`;
    try {
        const response = await apiClient.post<SubmittedLineupResponse>( url, payload, { headers: { Authorization: `Bearer ${token}` } });
        return response.data;
    } catch (error: any) {
        // Let network/timeout errors propagate unwrapped so callers can detect and retry
        if (isAxiosError(error) && !error.response) {
            throw error;
        }
        let errorMessage = 'Failed to submit lineup.'; let status: number | undefined;
         if (isAxiosError(error)) {
             status = error.response?.status;
             console.error("Axios error in submitLineup:", { message: error.message, url: error.config?.url, status, data: error.response?.data });
             const backendError = error.response?.data?.detail;
             if (typeof backendError === 'string') { errorMessage = backendError; }
             else if (status === 400) { errorMessage = 'Invalid lineup data. Please check salary cap, player count, positions, and duplicates.'; }
             else if (status === 401) { errorMessage = 'Authentication failed. Please log in again.'; }
             else if (status === 404) { errorMessage = `Contest ${payload.contest_id} not found.`; }
             else if (status === 422) { if (Array.isArray(backendError)) { try { errorMessage = backendError.map(e => `${e.loc?.join('.') || 'Input'} error: ${e.msg}`).join('; '); } catch (e) { /* ignore parse error */ } } else { errorMessage = "Invalid data format submitted."; } }
         } else { console.error("Unexpected error in submitLineup API call:", error); }
        const userMessage = status === 400
            ? errorMessage
            : status === 404
                ? 'This contest is no longer available.'
                : null;
        throw new ApiError(errorMessage, status, { cause: error, userMessage });
    }
};

export const updateMyLineup = async (
    lineupId: number,
    payload: LineupSubmissionPayload,
    token: string
): Promise<SubmittedLineupResponse> => {
    if (!token) throw new ApiError("Authentication required to update lineup.", 401);
    if (!lineupId) throw new Error("Lineup ID is required for update.");

    const url = `${API_BASE_URL}/dfs/lineups/${lineupId}`;
    console.log(`Attempting to update lineup ID: ${lineupId} via PUT ${url}`);

    try {
        const response = await apiClient.put<SubmittedLineupResponse>(url, payload, {
            headers: { Authorization: `Bearer ${token}` },
        });
        console.log(`Successfully updated lineup ID: ${lineupId}`);
        return response.data;
    } catch (error: any) {
        // Let network/timeout errors propagate unwrapped so callers can detect and retry
        if (isAxiosError(error) && !error.response) {
            throw error;
        }
        let errorMessage = 'Failed to update lineup.';
        let status: number | undefined;
        if (isAxiosError(error)) {
            status = error.response?.status;
            console.error("Axios error in updateMyLineup:", {
                message: error.message, url: error.config?.url, status, data: error.response?.data,
            });
            const backendError = error.response?.data?.detail;

            if (typeof backendError === 'string') {
                errorMessage = backendError;
            } else if (status === 400) { errorMessage = 'Invalid lineup data. Check salary/positions/duplicates or if contest started.'; }
            else if (status === 401) { errorMessage = 'Authentication failed. Please log in again.'; }
            else if (status === 403) { errorMessage = 'You do not have permission to edit this lineup.'; }
            else if (status === 404) { errorMessage = `Lineup ${lineupId} not found.`; }
            else if (status === 422) { if (Array.isArray(backendError)) { try { errorMessage = backendError.map(e => `${e.loc?.join('.') || 'Input'} error: ${e.msg}`).join('; '); } catch (e) { /* ignore */ } } else { errorMessage = "Invalid data format submitted."; } }
        } else { console.error("Unexpected error in updateMyLineup API call:", error); }
        const userMessage = status === 400 || status === 403
            ? errorMessage
            : status === 404
                ? 'This lineup is no longer available.'
                : null;
        throw new ApiError(errorMessage, status, { cause: error, userMessage });
    }
};

export const fetchMyTodaysLineup = async (token: string): Promise<SubmittedLineupResponse> => {
    if (!token) throw new ApiError("Authentication token is required.", 401);
    const url = `${API_BASE_URL}/dfs/contests/today/my-lineup`;
    try {
        const response = await apiClient.get<SubmittedLineupResponse>(url, { headers: { Authorization: `Bearer ${token}` } });
        return response.data;
    } catch (error: any) {
        let errorMessage = "Failed to fetch your lineup for today.";
        let status: number | undefined;
        if (isAxiosError(error)) {
             status = error.response?.status;
             console.error("Axios error in fetchMyTodaysLineup:", { message: error.message, status, data: error.response?.data });
             if (status === 404) { errorMessage = "Lineup not found for today."; }
             else { const backendError = error.response?.data?.detail; if (typeof backendError === 'string') errorMessage = backendError; }
        } else { console.error("Unexpected error fetching today's lineup:", error); }
        throw new ApiError(errorMessage, status);
    }
};

export const fetchMyLineupForContest = async (contestId: number, token: string): Promise<SubmittedLineupResponse> => {
    if (!token) throw new ApiError("Authentication token is required.", 401);
    if (!contestId || typeof contestId !== 'number' || contestId <= 0) { throw new Error("A valid Contest ID is required."); }
    const url = `${API_BASE_URL}/dfs/contests/${contestId}/my-lineup`;
    console.log(`Fetching user lineup for specific contest ${contestId} from ${url}`);
    try {
        const response = await apiClient.get<SubmittedLineupResponse>(url, { headers: { Authorization: `Bearer ${token}` } });
        console.log(`Found existing lineup ${response.data.id} for contest ${contestId}`);
        return response.data;
    } catch (error: any) {
        let errorMessage = `Failed to fetch lineup for contest ${contestId}.`; let status: number | undefined;
        if (isAxiosError(error)) {
             status = error.response?.status;
             console.error(`Axios error in fetchMyLineupForContest (${contestId}):`, { message: error.message, status, data: error.response?.data });
             if (status === 404) { errorMessage = `Lineup not found for contest ${contestId}.`; throw new ApiError(errorMessage, status, { cause: error }); }
             // --- ADDED 403 Check ---
             else if (status === 403) { errorMessage = `Access denied for contest ${contestId}.`; throw new ApiError(errorMessage, status, { cause: error }); }
             // --- END 403 Check ---
             else { const backendError = error.response?.data?.detail; if (typeof backendError === 'string') { errorMessage = backendError; } }
        } else { console.error(`Unexpected error fetching lineup for contest ${contestId}:`, error); if (error instanceof Error) { errorMessage = error.message; } }
        throw new ApiError(errorMessage, status, { cause: error });
    }
};

export const fetchTodaysRankings = async (token: string): Promise<RankingsResponse> => {
    if (!token) throw new ApiError("Authentication token is required.", 401);
    const url = `${API_BASE_URL}/dfs/contests/today/rankings`;
    try {
        const response = await apiClient.get<RankingsResponse>(url, { headers: { Authorization: `Bearer ${token}` } });
        return response.data;
    } catch (error: any) {
        let errorMessage = 'Failed to fetch rankings for today.'; let status: number | undefined;
        if (isAxiosError(error)) {
             status = error.response?.status;
             console.error("Axios error fetching today's rankings:", { message: error.message, status, data: error.response?.data });
             const backendError = error.response?.data?.detail;
             if (status === 403) { errorMessage = 'Access denied for today\'s rankings.'; } // Handle 403
             else if (typeof backendError === 'string') errorMessage = backendError;
        } else { console.error("Unexpected error fetching today's rankings:", error); }
        throw new ApiError(errorMessage, status);
    }
};

export const fetchContestRankings = async (
    contestId: number,
    token: string | null // <-- 1. Allow token to be null
): Promise<RankingsResponse> => {
    // 2. Remove any initial `if (!token)` check.
    if (!contestId || typeof contestId !== 'number' || contestId <= 0) {
        throw new Error("A valid Contest ID is required.");
    }
    const url = `${API_BASE_URL}/dfs/contests/${contestId}/rankings`;
    console.log(`Fetching rankings for specific contest ${contestId} from ${url}`);
    try {
        // 3. Conditionally build headers.
        const headers: { [key: string]: string } = {};
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await apiClient.get<RankingsResponse>(url, {
            headers: headers, // Use the new headers object
        });
        console.log(`Received ${response.data?.rankings?.length ?? 0} ranking entries for contest ${contestId}`);
        return response.data;
    } catch (error: any) {
        let errorMessage = `Failed to fetch rankings for contest ${contestId}.`;
        let status: number | undefined;
        if (isAxiosError(error)) {
             status = error.response?.status;
             console.error(`Axios error fetching rankings for contest ${contestId}:`, { message: error.message, status, data: error.response?.data });
             if (status === 404) { errorMessage = `Contest ${contestId} not found or no rankings available.`; }
             else if (status === 403) { errorMessage = `Access denied for contest ${contestId} rankings.`; }
             else { const backendError = error.response?.data?.detail; if (typeof backendError === 'string') errorMessage = backendError; }
        } else { console.error(`Unexpected error fetching rankings for contest ${contestId}:`, error); if (error instanceof Error) errorMessage = error.message; }
        throw new ApiError(errorMessage, status, { cause: error });
    }
};

export const fetchLineupById = async (
    lineupId: number,
    token: string | null
  ): Promise<SubmittedLineupResponse> => {
    if (!lineupId) {
      throw new ApiError("Lineup ID required.", 400);
    }
    const url = `${API_BASE_URL}/dfs/lineups/${lineupId}`;
    try {
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await apiClient.get<SubmittedLineupResponse>(url, {
        headers,
      });
      return response.data;
    } catch (error: any) {
        let errorMessage = `Failed to fetch lineup ${lineupId}.`;
        let status: number | undefined;
        if (isAxiosError(error)) {
            status = error.response?.status;
            if (status === 404) { errorMessage = `Lineup with ID ${lineupId} not found.`; }
            // --- ADDED 403 Check ---
            else if (status === 403) { errorMessage = `Access denied for lineup ${lineupId}.`; }
            // --- END 403 Check ---
            else { errorMessage = error.response?.data?.detail || errorMessage; }
        } else { errorMessage = (error as Error).message || errorMessage; }
        throw new ApiError(errorMessage, status, { cause: error });
    }
  };

  export const fetchPlayerScoreBreakdown = async (
    playerId: number,
    contestIdOrAlias: string | number,
    token: string | null,
    signal?: AbortSignal,
  ): Promise<ApiFantasyScore[]> => {
    if (!playerId || !contestIdOrAlias) {
      throw new ApiError("Player ID and Contest ID/Alias required.", 400);
    }
    const url = `${API_BASE_URL}/dfs/players/${playerId}/scores/${contestIdOrAlias}`;
    try {
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await apiClient.get<ApiFantasyScore[]>(url, {
        headers,
        signal,
      });
      return response.data;
    } catch (error: unknown) {
        if (isCancel(error)) throw error;
        let errorMessage = `Failed to fetch score breakdown for P:${playerId} C:${contestIdOrAlias}.`;
        let status: number | undefined;
        if (isAxiosError(error)) {
            status = error.response?.status;
            if (status === 404) { errorMessage = `Could not find player, contest, or scores for P:${playerId} C:${contestIdOrAlias}.`; }
            // --- ADDED 403 Check ---
            else if (status === 403) { errorMessage = `Access denied for score breakdown P:${playerId} C:${contestIdOrAlias}.`; }
            // --- END 403 Check ---
            else { errorMessage = error.response?.data?.detail || errorMessage; }
        } else if (error instanceof Error) { errorMessage = error.message || errorMessage; }
        throw new ApiError(errorMessage, status, { cause: error });
    }
  };

  export const fetchContestMatches = async (
    contestIdOrAlias: string | number,
    token: string | null // <-- 1. Allow token to be null
  ): Promise<ApiMatchInfo[]> => {
    // 2. Remove any initial `if (!token)` check.
    if (!contestIdOrAlias) {
      throw new Error("Contest ID or alias is required.");
    }
    const url = `${API_BASE_URL}/dfs/contests/${contestIdOrAlias}/matches`;
    try {
      console.log(`Fetching matches for contest '${contestIdOrAlias}' from: ${url}`);
      
      // 3. Conditionally build headers.
      const headers: { [key: string]: string } = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Contest matches can involve upstream lookups on a cold cache; give it
      // more room than the 5 s client default. Callers still degrade
      // gracefully on failure (see DfsContestPage / DfsLineupBuilderPage).
      const response = await apiClient.get<ApiMatchInfo[]>(url, {
        headers: headers, // Use the new headers object
        timeout: 15000,
      });
      console.log(`Received ${response.data.length} matches for contest '${contestIdOrAlias}'.`);
      return response.data;
    } catch (error: any) {
        let errorMessage = `Failed to load matches for contest '${contestIdOrAlias}'.`;
        let status: number | undefined;
        if (isAxiosError(error)) {
            status = error.response?.status;
            console.error("Axios error fetching contest matches:", { message: error.message, status, data: error.response?.data });
            if (status === 401 || status === 403) { errorMessage = 'Authentication/Authorization failed.'; }
            else if (status === 404) { errorMessage = `Contest '${contestIdOrAlias}' not found or has no matches.`; }
            else { const backendError = error.response?.data?.detail; if (typeof backendError === 'string') errorMessage = backendError; }
        } else { console.error("Unexpected error fetching contest matches:", error); }
        throw new ApiError(errorMessage, status, { cause: error });
    }
  };

  export const fetchAllPlayersForContest = async (
    contestIdOrAlias: string | number,
    token: string
  ): Promise<ApiPlayer[]> => {
    if (!token) {
      throw new ApiError("Authentication token is required to fetch players for contest teams.", 401);
    }
    if (!contestIdOrAlias) {
      throw new Error("Contest ID or alias is required.");
    }
    const url = `${API_BASE_URL}/dfs/contests/${contestIdOrAlias}/teams-players`;
    try {
      console.log(`Fetching ALL players for teams in contest '${contestIdOrAlias}' from: ${url}`);
      const response = await apiClient.get<ApiPlayer[]>(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log(`Received ${response.data.length} players associated with teams in contest '${contestIdOrAlias}'.`);
      const playersWithDefaults = response.data.map((player: ApiPlayer) => ({
        ...player,
        fppg: player.fppg ?? null,
        oprk: player.oprk ?? null,
      }));
      return playersWithDefaults;
    } catch (error: any) {
        let errorMessage = `Failed to load players for contest '${contestIdOrAlias}' teams.`;
        let status: number | undefined;
        if (isAxiosError(error)) {
            status = error.response?.status;
            console.error("Axios error fetching all players for contest:", { message: error.message, status, data: error.response?.data });
            if (status === 401 || status === 403) { errorMessage = 'Authentication/Authorization failed.'; }
            else if (status === 404) { errorMessage = `Contest '${contestIdOrAlias}' not found or no players associated with its teams.`; return []; } // Return empty for 404
            else { const backendError = error.response?.data?.detail; if (typeof backendError === 'string') errorMessage = backendError; }
        } else { console.error("Unexpected error fetching all players for contest:", error); }
        throw new ApiError(errorMessage, status);
    }
  };
// --- NEW FUNCTION TO FETCH PLAYERS FOR A SPECIFIC MATCH IN A CONTEST ---
export const fetchPlayersForMatchInContest = async (
  contestId: number,
  matchId: number,
  token?: string | null
): Promise<ApiPlayerInMatchWithScore[]> => { // Uses the new interface
  // Public endpoint (optional auth server-side) - logged-out visitors get the
  // match modal too; the token is sent only when present.
  if (!contestId || !matchId) {
    throw new Error("Contest ID and Match ID are required.");
  }

  const url = `${API_BASE_URL}/dfs/contests/${contestId}/matches/${matchId}/players`;
  console.log(`[DFS API] Fetching players for C:${contestId} M:${matchId} from ${url}`);

  try {
    const response = await apiClient.get<ApiPlayerInMatchWithScore[]>(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    console.log(`[DFS API] Received ${response.data.length} players for C:${contestId} M:${matchId}`);
    return response.data;
  } catch (error: any) {
    let errorMessage = `Failed to load players for match ${matchId} in contest ${contestId}.`;
    let status: number | undefined;
    if (isAxiosError(error)) {
      status = error.response?.status;
      console.error(`Axios error fetching players for C:${contestId} M:${matchId}:`, {
        message: error.message, url: error.config?.url, status, data: error.response?.data,
      });
      if (status === 404) {
        errorMessage = `Match ${matchId} or Contest ${contestId} not found, or no players available.`;
      } else if (status === 401 || status === 403) {
        errorMessage = 'Authentication/Authorization failed.';
      } else {
        const backendError = error.response?.data?.detail;
        if (typeof backendError === 'string') errorMessage = backendError;
      }
    } else {
      console.error(`Unexpected error fetching players for C:${contestId} M:${matchId}:`, error);
    }
    throw new ApiError(errorMessage, status);
  }
};
// --- END NEW FUNCTION ---

// --- SEASON-LONG (non-contest) MATCH BREAKDOWN ---
export interface MatchBreakdownResponse {
  match: ApiMatchInfo;
  players: ApiPlayerInMatchWithScore[];
}

export const fetchMatchBreakdown = async (
  matchId: number,
  token: string | null,
  /** Season-long league context: players come back stamped with owner team +
   *  add/claim availability for this league (members only when private). */
  leagueId?: number | null,
): Promise<MatchBreakdownResponse> => {
  if (!matchId) throw new Error("Match ID is required.");
  const url = `${API_BASE_URL}/stats/matches/${matchId}/breakdown`;
  try {
    const response = await apiClient.get<MatchBreakdownResponse>(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      params: leagueId != null ? { league_id: leagueId } : undefined,
    });
    return response.data;
  } catch (error: any) {
    let errorMessage = `Failed to load match breakdown for match ${matchId}.`;
    let status: number | undefined;
    if (isAxiosError(error)) {
      status = error.response?.status;
      if (status === 404) errorMessage = `Match ${matchId} not found.`;
      else {
        const backendError = error.response?.data?.detail;
        if (typeof backendError === 'string') errorMessage = backendError;
      }
    }
    throw new ApiError(errorMessage, status);
  }
};
// --- END SEASON-LONG MATCH BREAKDOWN ---

export const fetchAllCompetitions = async (
    activeOnly: boolean = true,
): Promise<ApiCompetitionSlim[]> => {
    const url = `${API_BASE_URL}/competitions/?active_only=${activeOnly}`;
    console.log(`[Competitions API] Fetching competitions: ${url}`);
    try {
        const response = await apiClient.get<ApiCompetitionSlim[]>(url);
        console.log(`[Competitions API] Received ${response.data.length} competitions.`);
        return response.data;
    } catch (error: any) {
        let errorMessage = `Failed to fetch competitions.`;
        let status: number | undefined;
        if (isAxiosError(error)) {
            status = error.response?.status;
            console.error(`[Competitions API] Axios error fetching competitions:`, {
                message: error.message, url: error.config?.url, status, data: error.response?.data,
            });
            const backendError = error.response?.data?.detail;
            if (typeof backendError === 'string') errorMessage = backendError;
        } else {
            console.error(`[Competitions API] Unexpected error fetching competitions:`, error);
        }
        throw new ApiError(errorMessage, status);
    }
};
// --- Opponent Defense ---

export interface TeamDefenseStats {
    team_sb_id: number;
    team_name: string;
    oprk: number | null;
    games_played: number | null;
    total_fp_allowed_pg: number | null;
    // Raw current-season actuals above; estimated OPRK evidence below.
    blended_fp_allowed_pg?: number | null;
    blended_avg_stats_allowed_pg?: Record<string, number>;
    avg_stats_allowed_pg: {
        Goal?: number;
        Shot?: number;
        ShotOnTarget?: number;
        SCA?: number;
        GCA?: number;
        ProgPass?: number;
        ProgCarry?: number;
        Dispossessed?: number;
        [key: string]: number | undefined;
    };
}

export const fetchContestOpponentDefense = async (
    contestId: number,
    token: string | null,
): Promise<TeamDefenseStats[]> => {
    const url = `${API_BASE_URL}/dfs/contests/${contestId}/opponent-defense`;
    try {
        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const response = await apiClient.get<TeamDefenseStats[]>(url, { headers });
        return response.data;
    } catch (error: any) {
        if (isAxiosError(error)) {
            const status = error.response?.status;
            const detail = error.response?.data?.detail;
            throw new ApiError(typeof detail === 'string' ? detail : 'Failed to fetch opponent defense data.', status);
        }
        throw new ApiError('Unexpected error fetching opponent defense data.');
    }
};
// --- Player Projections ---

export interface PlayerProjectionDetail {
    player_id: number;
    projected_stats: {
        Goal?: number;
        Shot?: number;
        ShotOnTarget?: number;
        SCA?: number;
        GCA?: number;
        ProgPass?: number;
        ProgCarry?: number;
        Dispossessed?: number;
    };
    opp_stat_ranks: {
        Goal?: number;
        Shot?: number;
        ShotOnTarget?: number;
        SCA?: number;
        GCA?: number;
        ProgPass?: number;
        ProgCarry?: number;
        Dispossessed?: number;
    };
}

export const fetchPlayerProjections = async (
    contestId: number,
    token: string | null,
): Promise<PlayerProjectionDetail[]> => {
    const url = `${API_BASE_URL}/dfs/contests/${contestId}/player-projections`;
    try {
        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const response = await apiClient.get<PlayerProjectionDetail[]>(url, { headers });
        return response.data;
    } catch (error: any) {
        if (isAxiosError(error)) {
            const status = error.response?.status;
            const detail = error.response?.data?.detail;
            throw new ApiError(typeof detail === 'string' ? detail : 'Failed to fetch player projections.', status);
        }
        throw new ApiError('Unexpected error fetching player projections.');
    }
};

// --- Matchup Preview (defensive ranks + top 5 projected players per team) ---

export const fetchContestMatchupPreview = async (
    contestId: number,
    token: string | null,
): Promise<ContestMatchupPreviewResponse> => {
    const url = `${API_BASE_URL}/dfs/contests/${contestId}/matchup-preview`;
    try {
        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const response = await apiClient.get<ContestMatchupPreviewResponse>(url, { headers });
        return response.data;
    } catch (error: any) {
        if (isAxiosError(error)) {
            const status = error.response?.status;
            const detail = error.response?.data?.detail;
            throw new ApiError(typeof detail === 'string' ? detail : 'Failed to fetch matchup preview.', status);
        }
        throw new ApiError('Unexpected error fetching matchup preview.');
    }
};
// --- END OF FILE frontend/src/api/dfs.ts ---
